// Discord Embedded App SDK - 1769474016 integration for Wordsheet
// Handles OAuth flow and user identity for Discord Activity

import { DiscordSDK } from '@discord/embedded-app-sdk';

let discordSdk = null;
let discordUser = null;
let discordAccessToken = null;
let guildNickname = null;
let firebaseUid = null;
let firebaseIdToken = null;
let tokenRefreshTimer = null;

/**
 * Initialize the Discord SDK and authenticate the user
 * @returns {Promise<{discordSdk: DiscordSDK, user: object, uid: string}>}
 */
export async function initDiscord() {
    const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;

    if (!clientId) {
        throw new Error('VITE_DISCORD_CLIENT_ID environment variable is not set');
    }

    // Create Discord SDK instance
    discordSdk = new DiscordSDK(clientId);

    // Wait for SDK to be ready
    await discordSdk.ready();
    console.log('Discord SDK ready');

    // Request authorization from Discord
    // guilds.members.read allows us to get server-specific nicknames
    const { code } = await discordSdk.commands.authorize({
        client_id: clientId,
        response_type: 'code',
        prompt: 'none',
        scope: ['identify', 'rpc.activities.write', 'guilds.members.read'],
    });

    console.log('Discord authorization code received');

    // Exchange the authorization code for an access token via our worker
    const tokenResponse = await fetch('/.proxy/api/token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
    });

    if (!tokenResponse.ok) {
        const error = await tokenResponse.text();
        throw new Error(`Token exchange failed: ${error}`);
    }

    const { access_token } = await tokenResponse.json();
    discordAccessToken = access_token;
    console.log('Discord access token received');

    // Authenticate with Discord using the access token
    const auth = await discordSdk.commands.authenticate({ access_token });

    discordUser = auth.user;
    console.log('Discord authenticated:', discordUser.username);

    // Try to get guild-specific nickname if we're in a guild
    if (discordSdk.guildId) {
        try {
            const memberResponse = await fetch(
                `https://discord.com/api/users/@me/guilds/${discordSdk.guildId}/member`,
                {
                    headers: {
                        Authorization: `Bearer ${access_token}`,
                    },
                }
            );
            if (memberResponse.ok) {
                const memberData = await memberResponse.json();
                if (memberData.nick) {
                    guildNickname = memberData.nick;
                    console.log('Guild nickname:', guildNickname);
                }
            }
        } catch (e) {
            console.log('Could not fetch guild member info:', e);
        }
    }

    // Exchange Discord token for Firebase ID token (done server-side to avoid CSP issues)
    const firebaseTokenResponse = await fetch('/.proxy/api/firebase-token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access_token }),
    });

    if (!firebaseTokenResponse.ok) {
        const error = await firebaseTokenResponse.text();
        throw new Error(`Firebase token exchange failed: ${error}`);
    }

    const { idToken, refreshToken, expiresIn, uid } = await firebaseTokenResponse.json();
    firebaseUid = uid;
    firebaseIdToken = idToken;
    console.log('Firebase ID token received, uid:', uid);

    // Set up token refresh before expiry (refresh at 50% of lifetime)
    const refreshMs = (parseInt(expiresIn, 10) * 1000) / 2;
    scheduleTokenRefresh(refreshToken, refreshMs);

    // Mark that we're in Discord context
    window.__DISCORD__ = true;

    return {
        discordSdk,
        user: discordUser,
        uid: firebaseUid,
    };
}

/**
 * Schedule token refresh
 */
function scheduleTokenRefresh(refreshToken, delayMs) {
    if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
    }

    tokenRefreshTimer = setTimeout(async () => {
        try {
            await refreshFirebaseToken(refreshToken);
        } catch (error) {
            console.error('Token refresh failed:', error);
        }
    }, delayMs);
}

/**
 * Refresh the Firebase ID token using the refresh token
 */
async function refreshFirebaseToken(refreshToken) {
    const response = await fetch('/.proxy/api/refresh-token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
        throw new Error('Failed to refresh token');
    }

    const { idToken, expiresIn, refreshToken: newRefreshToken } = await response.json();
    firebaseIdToken = idToken;
    console.log('Firebase ID token refreshed');

    // Schedule next refresh
    const refreshMs = (parseInt(expiresIn, 10) * 1000) / 2;
    scheduleTokenRefresh(newRefreshToken || refreshToken, refreshMs);
}

/**
 * Get the current Discord user (if authenticated)
 * @returns {object|null}
 */
export function getDiscordUser() {
    return discordUser;
}

/**
 * Get the Discord SDK instance
 * @returns {DiscordSDK|null}
 */
export function getDiscordSdk() {
    return discordSdk;
}

/**
 * Get the Discord Activity instance ID (same for all users in the same Activity instance)
 * @returns {string|null}
 */
export function getDiscordInstanceId() {
    return discordSdk?.instanceId || null;
}

/**
 * Get the Discord channel ID (stable across Activity relaunches from the same channel)
 * @returns {string|null}
 */
export function getDiscordChannelId() {
    return discordSdk?.channelId || null;
}

/**
 * Update the Discord Activity rich presence
 * @param {string} state - Game state ('In Lobby', 'Playing', 'Game Over')
 * @param {string|null} gameCode - The game code
 * @param {number} playerCount - Current number of players
 * @param {number} maxPlayers - Maximum players (default 5)
 * @param {object|null} gameEndInfo - Optional info for game over state
 * @param {object} gameEndInfo.winner - Winner player { nickname, score }
 * @param {array} gameEndInfo.players - Top players [{ nickname, score }, ...]
 */
export async function updateActivityState(state, gameCode, playerCount, maxPlayers = 5, gameEndInfo = null) {
    if (!discordSdk) {
        console.log('updateActivityState: No Discord SDK');
        return;
    }

    // If no game code, clear the activity
    let activity = null;

    if (gameCode) {
        activity = {
            type: 0, // Playing
            details: gameCode,
            party: {
                id: `wordsheet_${gameCode}`,
                size: [playerCount, maxPlayers]
            }
        };

        // Advertise late-join bonus during lobby/playing states
        if (!gameEndInfo) {
            activity.state = 'Late joiners spin for bonus points!';
        }

        // Add winner info for game over state
        if (gameEndInfo && gameEndInfo.winner) {
            // Truncate nickname if needed to fit within 128 char limit
            // Format: "🏆 {nickname} wins! ({score} pts)"
            // Reserve ~20 chars for the fixed text, leaving ~108 for nickname
            const maxNicknameLen = 100;
            let winnerName = gameEndInfo.winner.nickname || 'Unknown';
            if (winnerName.length > maxNicknameLen) {
                winnerName = winnerName.substring(0, maxNicknameLen - 1) + '…';
            }
            activity.state = `🏆 ${winnerName} wins! (${gameEndInfo.winner.score} pts)`;
        }
    }

    console.log('updateActivityState: Calling setActivity with:', activity);

    try {
        const result = await discordSdk.commands.setActivity({ activity });
        console.log('updateActivityState: Success, result:', result);
    } catch (e) {
        console.error('updateActivityState: Failed:', e);
    }
}

/**
 * Get the Firebase UID for the Discord user
 * @returns {string|null}
 */
export function getFirebaseUid() {
    return firebaseUid;
}

/**
 * Get the current Firebase ID token for API calls
 * @returns {string|null}
 */
export function getFirebaseIdToken() {
    return firebaseIdToken;
}

/**
 * Get the Discord user's avatar URL
 * @param {object} user - Discord user object
 * @param {number} size - Avatar size (default 64)
 * @returns {string|null}
 */
export function getAvatarUrl(user, size = 64) {
    if (!user) return null;

    if (user.avatar) {
        return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=${size}`;
    }

    // Default avatar based on discriminator or user ID
    const defaultIndex = user.discriminator
        ? parseInt(user.discriminator) % 5
        : (BigInt(user.id) >> 22n) % 6n;
    return `https://cdn.discordapp.com/embed/avatars/${defaultIndex}.png?size=${size}`;
}

/**
 * Get the display name for a Discord user
 * Prefers server nickname > global display name > username
 * @param {object} user - Discord user object
 * @returns {string}
 */
export function getDisplayName(user) {
    if (!user) return 'Unknown Player';

    // Prefer guild nickname (server-specific), then global_name, then username
    return guildNickname || user.global_name || user.username;
}

/**
 * Check if we're running in a Discord Activity context
 * @returns {boolean}
 */
export function isDiscordActivity() {
    // Check for Discord-specific URL patterns or SDK presence
    return window.location.href.includes('discordsays.com') ||
           window.location.href.includes('.discordsays.com') ||
           window.__DISCORD__ === true;
}
